import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import addressServices from '@src/services/address.services';

export const addressSave = createAsyncThunk(
  'addressSave',
  async (body, { getState, rejectWithValue }) => {
    try {
      const customerId = await getState()?.customer?.selectedCustomer?.customerId;
      if (!customerId) {
        return rejectWithValue({
          message: 'Please select a customer',
        });
      }
      return await addressServices.addressSave({ ...body, customerId: customerId });
    } catch (error) {
      return rejectWithValue(error?.data);
    }
  },
);

export const addressGetList = createAsyncThunk(
  'addressGetList',
  async (body, { getState, rejectWithValue }) => {
    try {
      const customerId = await getState()?.customer?.selectedCustomer?.customerId;
      const body = { CustomerId: customerId };
      return await addressServices.addressGetList(body);
    } catch (error) {
      return rejectWithValue(error?.data);
    }
  },
);

export const addressGetListByUserQuery = createAsyncThunk(
  'addressGetListByUserQuery',
  async (body, { getState, rejectWithValue }) => {
    try {
      const customerId = await getState()?.customer?.selectedCustomer?.customerId;
      return await addressServices.addressGetListByUserQuery(customerId);
    } catch (error) {
      return rejectWithValue(error?.data);
    }
  },
);

export const addressDelete = createAsyncThunk(
  'addressDelete',
  async (body, { getState, rejectWithValue }) => {
    try {
      const customerId = await getState()?.customer?.selectedCustomer?.customerId;
      return await addressServices.addressDelete({ ...body, customerId: customerId });
    } catch (error) {
      return rejectWithValue(error?.data);
    }
  },
);

export const addressUpdate = createAsyncThunk(
  'addressUpdate',
  async (body, { getState, rejectWithValue }) => {
    try {
      const customerId = await getState()?.customer?.selectedCustomer?.customerId;
      return await addressServices.addressUpdate({ ...body, customerId: customerId });
    } catch (error) {
      return rejectWithValue(error?.data);
    }
  },
);

export const addressGetPagedListByUserQuery = createAsyncThunk(
  'addressGetPagedListByUserQuery',
  async ({ body, pageNumber, pageSize }, { getState, rejectWithValue }) => {
    try {
      const customerId = await getState()?.customer?.selectedCustomer?.customerId;
      const data = {
        ...body,
        customerId: customerId,
        vouId: body?.vouId ? body?.vouId : 1,
      };
      return await addressServices.addressGetPagedListByUserQuery({
        data,
        pageNumber: pageNumber > -1 ? pageNumber : 1,
        pageSize: pageSize > -1 ? pageSize : 10,
      });
    } catch (error) {
      return rejectWithValue(error?.data);
    }
  },
);

export const getListAddresses = createAsyncThunk(
  'admin/getListAddresses',
  async ({ body, vouId }, { rejectWithValue }) => {
    try {
      return await addressServices.getListAddresses({ data: body, vouId });
    } catch (error) {
      return rejectWithValue(error?.data);
    }
  },
);

export const addressSlice = createSlice({
  name: 'address',
  initialState: {
    addressList: [],
    addressListByUserQuery: [],
    addressPagedList: [],
    response: {},
    filterCityList: {},
    filterCountyList: {},
    filterDeliveryOwnerList: {},
    filterNeighborhoodList: {},
    tableProperty: {
      currentPage: 1,
      pageSize: 10,
      totalCount: 1,
      totalPages: 1,
    },
    filterObject: {},
    addresses: [],
    selectedAddressType: {},
    tempFormList: {},
  },
  reducers: {
    resetResponse: (state) => {
      state.response = {};
    },
    setFilterObject: (state, action) => {
      state.filterObject = action.payload;
    },
    resetTableProperty: (state, action) => {
      state.tableProperty = { currentPage: 1, pageSize: 10, totalCount: 1, totalPages: 1 };
    },
    setTempFormList(state, action) {
      state.tempFormList = action.payload;
    },
    setAddressSelectionType(state, action) {
      state.selectedAddressType = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(addressGetList.fulfilled, (state, action) => {
      state.addressList = action?.payload?.data?.items || [];
    });
    builder.addCase(addressGetList.rejected, (state, action) => {
      state.response = action?.payload;
    });
    builder.addCase(addressGetListByUserQuery.fulfilled, (state, action) => {
      state.addressListByUserQuery = action?.payload?.data?.items || [];
      state.addressPagedList = action?.payload?.data?.items || [];
    });
    builder.addCase(addressGetListByUserQuery.rejected, (state, action) => {
      state.response = action?.payload;
      state.addressListByUserQuery = [];
      state.addressPagedList = [];
    });
    builder.addCase(addressGetPagedListByUserQuery.fulfilled, (state, action) => {
      state.addressPagedList = action?.payload?.data?.items || [];
      state.addressListByUserQuery = action?.payload?.data?.items || [];
      state.filterCityList = action?.payload?.data?.cityList || {};
      state.filterCountyList = action?.payload?.data?.countyList || {};
      state.filterDeliveryOwnerList = action?.payload?.data?.deliveryOwnerList || {};
      state.filterNeighborhoodList = action?.payload?.data?.neighborhoodList || {};
      state.tableProperty.pageSize = action?.payload?.pageSize || 0;
      state.tableProperty.totalItems = action?.payload?.totalItems || 0;
      state.tableProperty.totalPages = action?.payload?.totalPages || 0;
      state.tableProperty.currentPage = action?.payload?.currentPage || 1;
    });
    builder.addCase(addressGetPagedListByUserQuery.rejected, (state, action) => {
      state.addressPagedList = [];
      state.addressListByUserQuery = [];
      state.tableProperty = {
        currentPage: 1,
        page: 1,
        pageSize: 10,
        totalCount: 0,
        totalPages: 0,
      };
      state.filterCityList = {};
      state.filterCountyList = {};
      state.filterDeliveryOwnerList = {};
      state.filterNeighborhoodList = {};
    });
    builder.addCase(addressDelete.fulfilled, (state, action) => {
      state.response = action.payload;
    });
    builder.addCase(addressDelete.rejected, (state, action) => {
      state.response = action.payload;
    });
    builder.addCase(addressUpdate.fulfilled, (state, action) => {
      state.response = action.payload;
    });
    builder.addCase(addressUpdate.rejected, (state, action) => {
      state.response = action.payload;
    });
    builder.addCase(addressSave.fulfilled, (state, action) => {
      state.response = action.payload;
    });
    builder.addCase(addressSave.rejected, (state, action) => {
      state.response = action.payload;
    });
    builder.addCase(getListAddresses.fulfilled, (state, action) => {
      state.addresses = {
        ...state.addresses,
        [action?.meta?.arg?.vouId]: {
          addressSelectionType: false,
          data: action?.payload?.data?.items || [],
          pagedProperty: action?.payload?.data?.pagedProperty,
          searchText: action?.meta?.arg?.body?.address || null,
        },
      };
      const addresses =
        action?.payload?.data?.items
          .map((item) => {
            return item.isChecked && item.id;
          })
          .filter((item) => item) || [];
      const vouId = action?.meta?.arg?.vouId;
      const mergedList = addresses.concat(
        state.tempFormList[vouId]?.filter((item) => addresses.indexOf(item) < 0),
      );
      state.tempFormList = {
        ...state.tempFormList,
        [vouId]: mergedList.filter((item) => item),
      };
    });
  },
});

export const {
  resetResponse,
  setFilterObject,
  resetTableProperty,
  setTempFormList,
  setAddressSelectionType,
} = addressSlice.actions;
